import { useState, useEffect, useCallback } from 'react';
import {
  Plus, FileText, Search, Filter, Download, Loader2, X, Trash2, ArrowDown, ArrowUp,
  Pencil, History, Clock, Check, ChevronsUpDown, Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Popover, PopoverTrigger, PopoverContent
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '@/components/ui/command';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ledgersAPI, vendorsAPI, storesAPI, grnV2API } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const emptyItem = () => ({
  product_name: '',
  qty: 1,
  rate: 0,
  amount: 0,
  reason: ''
});

const DebitCreditNotesPage = () => {
  const { toast } = useToast();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filterPartyType, setFilterPartyType] = useState('all');
  const [filterNoteType, setFilterNoteType] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  
  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  
  // History dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyNote, setHistoryNote] = useState(null);
  const [form, setForm] = useState({
    note_type: 'credit_note',
    party_type: 'vendor',
    party_id: '',
    note_date: new Date().toISOString().slice(0, 10),
    reference_type: '',
    reference_id: '',
    reference_number: '',
    reason: '',
    notes: '',
    items: [emptyItem()],
  });
  
  // Party options
  const [vendors, setVendors] = useState([]);
  const [stores, setStores] = useState([]);

  // GRN picker (Create dialog) — searchable dropdown of last 6mo Completed GRNs
  // for the selected vendor when reference_type = grn_local / grn_international
  const [grnOptions, setGrnOptions] = useState([]);
  const [grnPickerLoading, setGrnPickerLoading] = useState(false);
  const [grnPickerOpen, setGrnPickerOpen] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState(null); // full GRN doc when picked
  const [grnItemPicks, setGrnItemPicks] = useState({}); // { item_id: {selected, qty, rate} }

  // Line entry mode: 'items' (per-line) vs 'amount' (single lump sum)
  const [lineMode, setLineMode] = useState('items');
  const [directAmount, setDirectAmount] = useState('');
  
  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: 1, page_size: 100 };
      if (filterPartyType !== 'all') params.party_type = filterPartyType;
      if (filterNoteType !== 'all') params.note_type = filterNoteType;
      const res = await ledgersAPI.listAdjustmentNotes(params);
      let items = res.data?.items || [];
      if (filterSearch.trim()) {
        const q = filterSearch.trim().toLowerCase();
        items = items.filter(n =>
          (n.note_number || '').toLowerCase().includes(q) ||
          (n.party_name || '').toLowerCase().includes(q) ||
          (n.reference_number || '').toLowerCase().includes(q) ||
          (n.reason || '').toLowerCase().includes(q)
        );
      }
      setNotes(items);
      setTotal(items.length);
    } catch (e) {
      toast({
        title: 'Failed to load notes',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [filterPartyType, filterNoteType, filterSearch, toast]);
  
  const fetchParties = useCallback(async () => {
    try {
      const [vRes, sRes] = await Promise.all([
        vendorsAPI.list({ is_active: true }),
        storesAPI.list()
      ]);
      setVendors(vRes.data || []);
      setStores((sRes.data || []).filter(s => s.store_type === 'franchise'));
    } catch (e) {
      // Non-blocking
    }
  }, []);
  
  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => { fetchParties(); }, [fetchParties]);

  // Fetch last-6-months COMPLETED GRNs for the selected vendor & GRN type
  const fetchGrnOptions = useCallback(async (vendorId, refType) => {
    if (!vendorId || (refType !== 'grn_local' && refType !== 'grn_international')) {
      setGrnOptions([]);
      return;
    }
    try {
      setGrnPickerLoading(true);
      const twentyFourMonthsAgo = new Date();
      twentyFourMonthsAgo.setMonth(twentyFourMonthsAgo.getMonth() - 24);
      const from_date = twentyFourMonthsAgo.toISOString().slice(0, 10);
      const params = { vendor_id: vendorId, status: 'completed', from_date };
      const res = refType === 'grn_local'
        ? await grnV2API.listLocal(params)
        : await grnV2API.listInternational(params);
      setGrnOptions(res.data || []);
    } catch (e) {
      setGrnOptions([]);
      toast({
        title: 'Failed to load GRNs',
        description: e.response?.data?.detail || 'Could not fetch recent completed GRNs',
        variant: 'destructive'
      });
    } finally {
      setGrnPickerLoading(false);
    }
  }, [toast]);

  // Refetch whenever vendor or ref_type changes; clear picked GRN
  useEffect(() => {
    setSelectedGrn(null);
    setGrnItemPicks({});
    if (form.party_type === 'vendor' && form.party_id &&
        (form.reference_type === 'grn_local' || form.reference_type === 'grn_international')) {
      fetchGrnOptions(form.party_id, form.reference_type);
    } else {
      setGrnOptions([]);
    }
  }, [form.party_type, form.party_id, form.reference_type, fetchGrnOptions]);

  // When a GRN is picked, load full details (items) and reset selections
  const handlePickGrn = async (grnSummary) => {
    setGrnPickerOpen(false);
    try {
      setGrnPickerLoading(true);
      const res = form.reference_type === 'grn_local'
        ? await grnV2API.getLocal(grnSummary.id)
        : await grnV2API.getInternational(grnSummary.id);
      const full = res.data;
      setSelectedGrn(full);
      setGrnItemPicks({});
      // Wire reference_id / number into the form
      setForm(f => ({
        ...f,
        reference_id: full.id,
        reference_number: full.grn_number || ''
      }));
    } catch (e) {
      toast({
        title: 'Could not load GRN',
        description: e.response?.data?.detail || 'Try again',
        variant: 'destructive'
      });
    } finally {
      setGrnPickerLoading(false);
    }
  };

  const toggleGrnItem = (item) => {
    setGrnItemPicks(prev => {
      const cur = prev[item.item_id];
      if (cur) {
        const { [item.item_id]: _drop, ...rest } = prev;
        return rest;
      }
      const rate = Number(item.basic_cost || item.unit_price || 0);
      const qty = Number(item.accepted_qty || item.received_qty || 0);
      return { ...prev, [item.item_id]: { qty, rate } };
    });
  };

  const updateGrnItemPick = (itemId, field, value) => {
    setGrnItemPicks(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { qty: 0, rate: 0 }), [field]: value }
    }));
  };

  // Push selected GRN items into the note's line items
  const importGrnItemsToNote = () => {
    if (!selectedGrn) return;
    const picked = (selectedGrn.items || []).filter(it => grnItemPicks[it.item_id]);
    if (picked.length === 0) {
      toast({ title: 'No items selected', description: 'Tick at least one item to import', variant: 'destructive' });
      return;
    }
    const newItems = picked.map(it => {
      const pick = grnItemPicks[it.item_id] || {};
      const qty = parseFloat(pick.qty) || 0;
      const rate = parseFloat(pick.rate) || 0;
      return {
        product_id: it.product_id,
        product_name: `${it.product_name}${it.sku ? ` (${it.sku})` : ''}${it.lot_batch_number ? ` · Batch ${it.lot_batch_number}` : ''}`,
        qty,
        rate,
        amount: Math.round(qty * rate * 100) / 100,
        reason: ''
      };
    });
    setForm(f => ({ ...f, items: newItems }));
    toast({
      title: 'Items imported',
      description: `${newItems.length} line${newItems.length > 1 ? 's' : ''} pulled from GRN ${selectedGrn.grn_number}`
    });
  };

  
  const resetForm = () => {
    setForm({
      note_type: 'credit_note',
      party_type: 'vendor',
      party_id: '',
      note_date: new Date().toISOString().slice(0, 10),
      reference_type: '',
      reference_id: '',
      reference_number: '',
      reason: '',
      notes: '',
      items: [emptyItem()],
    });
    setSelectedGrn(null);
    setGrnItemPicks({});
    setGrnOptions([]);
    setLineMode('items');
    setDirectAmount('');
  };
  
  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'qty' || field === 'rate') {
        const qty = parseFloat(items[idx].qty) || 0;
        const rate = parseFloat(items[idx].rate) || 0;
        items[idx].amount = Math.round(qty * rate * 100) / 100;
      }
      return { ...prev, items };
    });
  };
  
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));
  const removeItem = (idx) => setForm(prev => ({
    ...prev,
    items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== idx) : prev.items
  }));
  
  const itemsSubtotal = form.items.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);
  const subtotal = lineMode === 'amount' ? (parseFloat(directAmount) || 0) : itemsSubtotal;
  const totalAmount = Math.round(subtotal * 100) / 100;
  
  // ---- Edit / History helpers ----
  const isWithin24h = (createdAt) => {
    if (!createdAt) return false;
    try {
      const created = new Date(createdAt);
      return (Date.now() - created.getTime()) < 24 * 60 * 60 * 1000;
    } catch { return false; }
  };
  
  const hoursLeft = (createdAt) => {
    if (!createdAt) return 0;
    try {
      const created = new Date(createdAt);
      const ms = 24 * 60 * 60 * 1000 - (Date.now() - created.getTime());
      return Math.max(0, Math.round(ms / (60 * 60 * 1000) * 10) / 10);
    } catch { return 0; }
  };
  
  const openEditDialog = (note) => {
    setEditingNote(note);
    setEditForm({
      note_date: note.note_date,
      reference_type: note.reference_type || '',
      reference_number: note.reference_number || '',
      reason: note.reason || '',
      notes: note.notes || '',
      items: (note.items && note.items.length > 0) ? note.items.map(it => ({
        product_name: it.product_name || '',
        qty: it.qty || 0,
        rate: it.rate || 0,
        amount: it.amount || 0,
      })) : [emptyItem()]
    });
    setEditReason('');
    setEditOpen(true);
  };
  
  const updateEditItem = (idx, field, value) => {
    setEditForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'qty' || field === 'rate') {
        const qty = parseFloat(items[idx].qty) || 0;
        const rate = parseFloat(items[idx].rate) || 0;
        items[idx].amount = Math.round(qty * rate * 100) / 100;
      }
      return { ...prev, items };
    });
  };
  
  const addEditItem = () => setEditForm(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));
  const removeEditItem = (idx) => setEditForm(prev => ({
    ...prev,
    items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== idx) : prev.items
  }));
  
  const editTotal = editForm
    ? Math.round(editForm.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0) * 100) / 100
    : 0;
  
  const handleEditSave = async () => {
    if (!editReason.trim()) {
      toast({ title: 'Reason required', description: 'Explain why this note is being edited', variant: 'destructive' });
      return;
    }
    if (editTotal <= 0) {
      toast({ title: 'Amount required', description: 'Total must be greater than 0', variant: 'destructive' });
      return;
    }
    try {
      setEditSaving(true);
      await ledgersAPI.updateAdjustmentNote(editingNote.id, {
        note_date: editForm.note_date,
        reference_type: editForm.reference_type || null,
        reference_number: editForm.reference_number || null,
        items: editForm.items.map(it => ({
          product_name: it.product_name,
          qty: parseFloat(it.qty) || 0,
          rate: parseFloat(it.rate) || 0,
          amount: parseFloat(it.amount) || 0,
        })),
        subtotal: editTotal,
        tax_amount: 0,
        total_amount: editTotal,
        reason: editForm.reason,
        notes: editForm.notes || null,
        edit_reason: editReason.trim(),
      });
      toast({
        title: 'Note updated',
        description: `${editingNote.note_number} has been edited and ledger re-posted`
      });
      setEditOpen(false);
      setEditingNote(null);
      setEditForm(null);
      setEditReason('');
      fetchNotes();
    } catch (e) {
      toast({
        title: 'Edit failed',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setEditSaving(false);
    }
  };
  
  const openHistory = (note) => {
    setHistoryNote(note);
    setHistoryOpen(true);
  };
  
  const handleCreate = async () => {
    if (!form.party_id) {
      toast({ title: 'Select party', description: 'Choose a vendor or franchise', variant: 'destructive' });
      return;
    }
    if (!form.reason.trim()) {
      toast({ title: 'Reason required', description: 'Enter a short reason for this note', variant: 'destructive' });
      return;
    }
    if (totalAmount <= 0) {
      toast({ title: 'Amount required', description: 'Total must be greater than 0', variant: 'destructive' });
      return;
    }
    
    try {
      setCreating(true);
      const payloadItems = lineMode === 'amount'
        ? [{ product_name: form.reason || 'Adjustment', qty: 1, rate: totalAmount, amount: totalAmount, reason: form.reason }]
        : form.items.map(it => ({
            product_name: it.product_name,
            qty: parseFloat(it.qty) || 0,
            rate: parseFloat(it.rate) || 0,
            amount: parseFloat(it.amount) || 0,
            reason: it.reason
          }));
      const payload = {
        note_type: form.note_type,
        party_type: form.party_type,
        party_id: form.party_id,
        note_date: form.note_date,
        reference_type: form.reference_type || null,
        reference_id: form.reference_id || null,
        reference_number: form.reference_number || null,
        items: payloadItems,
        subtotal: totalAmount,
        tax_amount: 0,
        total_amount: totalAmount,
        reason: form.reason,
        notes: form.notes || null
      };
      const res = await ledgersAPI.createAdjustmentNote(payload);
      toast({
        title: 'Note posted',
        description: `${res.data.note_number} posted to ledger successfully`
      });
      setCreateOpen(false);
      resetForm();
      fetchNotes();
    } catch (e) {
      toast({
        title: 'Failed to create note',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  };
  
  const openPDF = (note) => {
    const token = localStorage.getItem('token');
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/ledgers/adjustment-notes/${note.id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };
  
  const summary = {
    total_credit: notes.filter(n => n.note_type === 'credit_note').reduce((s, n) => s + (n.total_amount || 0), 0),
    total_debit: notes.filter(n => n.note_type === 'debit_note').reduce((s, n) => s + (n.total_amount || 0), 0),
    count_credit: notes.filter(n => n.note_type === 'credit_note').length,
    count_debit: notes.filter(n => n.note_type === 'debit_note').length,
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Toaster />
      
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Debit / Credit Notes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Post adjustments against vendor bills or franchise invoices - directly reflects in the ledger.
          </p>
        </div>
        <Button onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="create-note-btn">
          <Plus className="w-4 h-4 mr-2" /> New Note
        </Button>
      </div>
      
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="pt-4">
            <p className="text-xs text-emerald-700">Credit Notes Value</p>
            <p className="text-2xl font-bold text-emerald-800">
              ₹{Math.round(summary.total_credit || 0).toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-gray-500 mt-1">{summary.count_credit} note(s)</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="pt-4">
            <p className="text-xs text-red-700">Debit Notes Value</p>
            <p className="text-2xl font-bold text-red-800">
              ₹{Math.round(summary.total_debit || 0).toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-gray-500 mt-1">{summary.count_debit} note(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-600">Total Notes</p>
            <p className="text-2xl font-bold text-gray-800">{total}</p>
            <p className="text-xs text-gray-500 mt-1">In selected filters</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-600">Net Ledger Impact</p>
            <p className="text-2xl font-bold text-gray-800">
              ₹{Math.round((summary.total_debit || 0) - (summary.total_credit || 0)).toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-gray-500 mt-1">Debit − Credit</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Party Type</Label>
              <Select value={filterPartyType} onValueChange={setFilterPartyType}>
                <SelectTrigger data-testid="filter-party-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Parties</SelectItem>
                  <SelectItem value="vendor">Vendors</SelectItem>
                  <SelectItem value="franchise">Franchises</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note Type</Label>
              <Select value={filterNoteType} onValueChange={setFilterNoteType}>
                <SelectTrigger data-testid="filter-note-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Notes</SelectItem>
                  <SelectItem value="credit_note">Credit Notes</SelectItem>
                  <SelectItem value="debit_note">Debit Notes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <Input
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  placeholder="Note No, Party, Reference, Reason"
                  className="pl-8"
                  data-testid="filter-search"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Notes</CardTitle>
          <CardDescription>{total} record(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            </div>
          ) : notes.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No notes match the current filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Type</TableHead>
                  <TableHead>Note No</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Party</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notes.map(n => {
                  const isCredit = n.note_type === 'credit_note';
                  return (
                    <TableRow key={n.id} data-testid={`note-row-${n.note_number}`}>
                      <TableCell>
                        <Badge className={isCredit ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}>
                          {isCredit ? <ArrowDown className="w-3 h-3 mr-1" /> : <ArrowUp className="w-3 h-3 mr-1" />}
                          {isCredit ? 'Credit' : 'Debit'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{n.note_number}</TableCell>
                      <TableCell className="text-gray-600">
                        {n.note_date ? formatDate(n.note_date) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{n.party_name}</div>
                        <div className="text-xs text-gray-500 capitalize">{n.party_type}</div>
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">
                        {n.reference_number || <span className="text-gray-400">Standalone</span>}
                      </TableCell>
                      <TableCell className="text-sm text-gray-700 max-w-[240px] truncate" title={n.reason}>
                        {n.reason}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        ₹{Math.round(Number(n.total_amount || 0)).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-xs">
                          {(n.status || 'draft').toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openPDF(n)}
                            data-testid={`download-pdf-${n.note_number}`}
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {isWithin24h(n.created_at) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(n)}
                              data-testid={`edit-note-${n.note_number}`}
                              title={`Edit (${hoursLeft(n.created_at)}h left)`}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {n.edit_history && n.edit_history.length > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openHistory(n)}
                              data-testid={`history-note-${n.note_number}`}
                              title="View edit history"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                            >
                              <History className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setEditingNote(null); setEditForm(null); } }}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-note-dialog">
          <DialogHeader>
            <DialogTitle>
              Edit {editingNote?.note_number}
              {editingNote && (
                <span className="ml-2 text-xs text-amber-700 font-normal">
                  <Clock className="inline w-3 h-3 mr-1" />
                  {hoursLeft(editingNote.created_at)}h left in edit window
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Editing will reverse the original ledger entry and post a fresh one. Every edit is
              recorded in an audit trail.
            </DialogDescription>
          </DialogHeader>
          
          {editForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Note Date *</Label>
                  <Input
                    type="date"
                    value={editForm.note_date}
                    onChange={(e) => setEditForm(f => ({ ...f, note_date: e.target.value }))}
                    data-testid="edit-note-date"
                  />
                </div>
                <div>
                  <Label>Reason *</Label>
                  <Input
                    value={editForm.reason}
                    onChange={(e) => setEditForm(f => ({ ...f, reason: e.target.value }))}
                    placeholder="Short reason for the note"
                    data-testid="edit-note-reason"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Reference Type (Optional)</Label>
                  <Select
                    value={editForm.reference_type || 'none'}
                    onValueChange={(v) => setEditForm(f => ({ ...f, reference_type: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Standalone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Standalone</SelectItem>
                      <SelectItem value="grn_local">Local GRN</SelectItem>
                      <SelectItem value="grn_international">International GRN</SelectItem>
                      <SelectItem value="invoice">Invoice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference Number (Optional)</Label>
                  <Input
                    value={editForm.reference_number}
                    onChange={(e) => setEditForm(f => ({ ...f, reference_number: e.target.value }))}
                    disabled={!editForm.reference_type}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Line Items</Label>
                  <Button size="sm" variant="outline" onClick={addEditItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Line
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-xs text-gray-600">
                        <th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2 text-right w-20">Qty</th>
                        <th className="px-3 py-2 text-right w-28">Rate</th>
                        <th className="px-3 py-2 text-right w-28">Amount</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editForm.items.map((it, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-2 py-2">
                            <Input
                              value={it.product_name}
                              onChange={(e) => updateEditItem(idx, 'product_name', e.target.value)}
                              className="h-8 border-0 focus-visible:ring-0"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <Input
                              type="number"
                              value={it.qty}
                              onChange={(e) => updateEditItem(idx, 'qty', e.target.value)}
                              className="h-8 text-right"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <Input
                              type="number"
                              value={it.rate}
                              onChange={(e) => updateEditItem(idx, 'rate', e.target.value)}
                              className="h-8 text-right"
                            />
                          </td>
                          <td className="px-2 py-2 text-right font-mono">
                            ₹{(it.amount || 0).toFixed(2)}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {editForm.items.length > 1 && (
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeEditItem(idx)}>
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between mt-3">
                  <div className="text-xs text-gray-500">
                    Prior total: ₹{Math.round(Number(editingNote?.total_amount || 0)).toLocaleString('en-IN')}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">New Total</p>
                    <p className="text-xl font-bold" data-testid="edit-total">
                      ₹{Math.round(editTotal || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Additional Notes (Optional)</Label>
                <Input
                  value={editForm.notes}
                  onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Label className="text-amber-900 font-semibold">Reason for this Edit * (audit trail)</Label>
                <Input
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="e.g. Corrected qty, wrong rate, missing item"
                  className="mt-1 bg-white"
                  data-testid="edit-reason-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving} data-testid="save-edit-btn">
              {editSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />}
              Save & Re-post to Ledger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle>Edit History - {historyNote?.note_number}</DialogTitle>
            <DialogDescription>
              All edits are recorded with a timestamp, editor, and before/after snapshot.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {(historyNote?.edit_history || []).length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">No edits recorded.</div>
            ) : (
              historyNote.edit_history.map((h, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-800">
                      Edit #{idx + 1} by {h.edited_by_name || h.edited_by}
                    </div>
                    <div className="text-xs text-gray-500">
                      {h.edited_at ? new Date(h.edited_at).toLocaleString('en-IN') : ''}
                    </div>
                  </div>
                  <div className="p-2 bg-amber-50 rounded text-xs">
                    <strong>Reason:</strong> {h.edit_reason}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="border rounded p-2 bg-red-50/40">
                      <p className="font-semibold text-red-700 mb-1">Before</p>
                      <div>Date: {h.before?.note_date}</div>
                      <div>Reason: {h.before?.reason}</div>
                      <div>Total: ₹{Math.round(Number(h.before?.total_amount || 0)).toLocaleString('en-IN')}</div>
                      <div>Items: {(h.before?.items || []).length} line(s)</div>
                    </div>
                    <div className="border rounded p-2 bg-emerald-50/40">
                      <p className="font-semibold text-emerald-700 mb-1">After</p>
                      <div>Date: {h.after?.note_date}</div>
                      <div>Reason: {h.after?.reason}</div>
                      <div>Total: ₹{Math.round(Number(h.after?.total_amount || 0)).toLocaleString('en-IN')}</div>
                      <div>Items: {(h.after?.items || []).length} line(s)</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-note-dialog">
          <DialogHeader>
            <DialogTitle>New {form.note_type === 'credit_note' ? 'Credit' : 'Debit'} Note</DialogTitle>
            <DialogDescription>
              Note will be posted to the party's ledger immediately on save.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Note Type + Party Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Note Type *</Label>
                <RadioGroup
                  value={form.note_type}
                  onValueChange={(v) => setForm(f => ({ ...f, note_type: v }))}
                  className="flex gap-4 mt-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="credit_note" id="cn" data-testid="radio-credit-note" />
                    <Label htmlFor="cn" className="cursor-pointer flex items-center gap-1">
                      <ArrowDown className="w-4 h-4 text-emerald-600" /> Credit Note
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="debit_note" id="dn" data-testid="radio-debit-note" />
                    <Label htmlFor="dn" className="cursor-pointer flex items-center gap-1">
                      <ArrowUp className="w-4 h-4 text-red-600" /> Debit Note
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              <div>
                <Label>Against *</Label>
                <RadioGroup
                  value={form.party_type}
                  onValueChange={(v) => setForm(f => ({
                    ...f,
                    party_type: v,
                    party_id: '',
                    // Franchise doesn't have GRN references — clear if switching
                    reference_type: (v === 'franchise' && (f.reference_type === 'grn_local' || f.reference_type === 'grn_international')) ? '' : f.reference_type,
                    reference_id: '',
                    reference_number: ''
                  }))}
                  className="flex gap-4 mt-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="vendor" id="pv" data-testid="radio-vendor" />
                    <Label htmlFor="pv" className="cursor-pointer">Vendor</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="franchise" id="pf" data-testid="radio-franchise" />
                    <Label htmlFor="pf" className="cursor-pointer">Franchise</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
            
            {/* Party + Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{form.party_type === 'vendor' ? 'Vendor *' : 'Franchise Store *'}</Label>
                <Select
                  value={form.party_id}
                  onValueChange={(v) => setForm(f => ({ ...f, party_id: v, reference_id: '', reference_number: '' }))}
                >
                  <SelectTrigger data-testid="select-party">
                    <SelectValue placeholder={`Select ${form.party_type}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(form.party_type === 'vendor' ? vendors : stores).map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.code ? `(${p.code})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note Date *</Label>
                <Input
                  type="date"
                  value={form.note_date}
                  onChange={(e) => setForm(f => ({ ...f, note_date: e.target.value }))}
                  data-testid="input-note-date"
                />
              </div>
            </div>
            
            {/* Reference (optional) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reference Type (Optional)</Label>
                <Select
                  value={form.reference_type || 'none'}
                  onValueChange={(v) => setForm(f => ({ ...f, reference_type: v === 'none' ? '' : v, reference_id: '', reference_number: '' }))}
                >
                  <SelectTrigger data-testid="select-ref-type">
                    <SelectValue placeholder="Standalone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Standalone (no reference)</SelectItem>
                    {form.party_type === 'vendor' && (
                      <>
                        <SelectItem value="grn_local">Local GRN</SelectItem>
                        <SelectItem value="grn_international">International GRN</SelectItem>
                      </>
                    )}
                    <SelectItem value="invoice">Invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>
                  Reference Number
                  {(form.reference_type === 'grn_local' || form.reference_type === 'grn_international') && (
                    <span className="ml-1 text-xs text-gray-500">(pick from last 24 months completed GRNs)</span>
                  )}
                </Label>
                {(form.reference_type === 'grn_local' || form.reference_type === 'grn_international') && form.party_type === 'vendor' ? (
                  <Popover open={grnPickerOpen} onOpenChange={setGrnPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        disabled={!form.party_id}
                        className="w-full justify-between font-normal"
                        data-testid="grn-picker-btn"
                      >
                        {selectedGrn ? (
                          <span className="flex items-center gap-2 truncate">
                            <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                            <span className="truncate">{selectedGrn.grn_number}</span>
                            <span className="text-xs text-gray-500 shrink-0">
                              · {formatDate(selectedGrn.received_date)}
                            </span>
                          </span>
                        ) : (
                          <span className="text-gray-400">
                            {form.party_id ? 'Search & select a GRN…' : 'Pick vendor first'}
                          </span>
                        )}
                        <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[420px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search by GRN number, invoice, PO…" />
                        <CommandList>
                          {grnPickerLoading ? (
                            <div className="p-4 text-sm text-gray-500 flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" /> Loading recent GRNs…
                            </div>
                          ) : (
                            <>
                              <CommandEmpty>No completed GRNs in the last 24 months.</CommandEmpty>
                              <CommandGroup heading={`${grnOptions.length} completed GRN${grnOptions.length === 1 ? '' : 's'}`}>
                                {grnOptions.map(g => (
                                  <CommandItem
                                    key={g.id}
                                    value={`${g.grn_number} ${g.invoice_number || ''} ${g.po_number || ''}`}
                                    onSelect={() => handlePickGrn(g)}
                                    className="flex items-start justify-between gap-3"
                                    data-testid={`grn-opt-${g.grn_number}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <Check className={`w-4 h-4 ${selectedGrn?.id === g.id ? 'opacity-100 text-emerald-600' : 'opacity-0'}`} />
                                        <span className="font-medium">{g.grn_number}</span>
                                        <Badge variant="outline" className="text-[10px]">
                                          {formatDate(g.received_date)}
                                        </Badge>
                                      </div>
                                      <div className="text-[11px] text-gray-500 ml-6 mt-0.5 truncate">
                                        {g.invoice_number ? `Inv: ${g.invoice_number}` : ''}
                                        {g.po_number ? ` · PO: ${g.po_number}` : ''}
                                        {g.grand_total ? ` · ₹${Math.round(Number(g.grand_total)).toLocaleString('en-IN')}` : ''}
                                      </div>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Input
                    value={form.reference_number}
                    onChange={(e) => setForm(f => ({ ...f, reference_number: e.target.value }))}
                    placeholder="e.g. INV-2026-001"
                    data-testid="input-ref-number"
                    disabled={!form.reference_type}
                  />
                )}
              </div>
            </div>

            {/* GRN Line-item picker */}
            {lineMode === 'items' && selectedGrn && (form.reference_type === 'grn_local' || form.reference_type === 'grn_international') && (
              <div className="border rounded-lg bg-blue-50/40 border-blue-200 p-3 space-y-2" data-testid="grn-item-picker">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <Package className="w-4 h-4" />
                    <span className="font-semibold">Pick line items from {selectedGrn.grn_number}</span>
                    <span className="text-xs text-blue-600">
                      · {(selectedGrn.items || []).length} line{(selectedGrn.items || []).length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={importGrnItemsToNote}
                    disabled={Object.keys(grnItemPicks).length === 0}
                    data-testid="import-grn-items-btn"
                  >
                    <ArrowDown className="w-4 h-4 mr-1" /> Import selected ({Object.keys(grnItemPicks).length})
                  </Button>
                </div>
                <div className="max-h-56 overflow-y-auto border rounded bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="text-left text-xs text-gray-600">
                        <th className="px-2 py-1.5 w-8"></th>
                        <th className="px-2 py-1.5">Product</th>
                        <th className="px-2 py-1.5 text-right w-24">GRN Qty</th>
                        <th className="px-2 py-1.5 text-right w-24">Note Qty</th>
                        <th className="px-2 py-1.5 text-right w-28">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedGrn.items || []).map(it => {
                        const picked = grnItemPicks[it.item_id];
                        return (
                          <tr key={it.item_id} className={`border-t ${picked ? 'bg-emerald-50/40' : ''}`}>
                            <td className="px-2 py-1.5">
                              <Checkbox
                                checked={!!picked}
                                onCheckedChange={() => toggleGrnItem(it)}
                                data-testid={`grn-item-check-${it.sku}`}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="font-medium truncate max-w-[220px]">{it.product_name}</div>
                              <div className="text-[10px] text-gray-500">
                                {it.sku}{it.lot_batch_number ? ` · Batch ${it.lot_batch_number}` : ''}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-right text-gray-600">
                              {it.accepted_qty || it.received_qty || 0}
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number"
                                min={0}
                                max={it.accepted_qty || it.received_qty || undefined}
                                value={picked?.qty ?? ''}
                                onChange={(e) => updateGrnItemPick(it.item_id, 'qty', e.target.value)}
                                disabled={!picked}
                                className="h-7 text-right"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number"
                                value={picked?.rate ?? ''}
                                onChange={(e) => updateGrnItemPick(it.item_id, 'rate', e.target.value)}
                                disabled={!picked}
                                className="h-7 text-right"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-gray-500">
                  Rate defaults to the batch's basic cost. Adjust qty and rate to reflect exactly what's being credited/debited.
                </p>
              </div>
            )}
            
            {/* Reason */}
            <div>
              <Label>Reason *</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Damaged goods returned, rate difference, discount granted"
                data-testid="input-reason"
              />
            </div>
            
            {/* Items — line-item breakdown OR direct amount */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Label className="mb-0">Line Details</Label>
                  <div className="flex items-center gap-3 text-xs">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="line-mode"
                        checked={lineMode === 'items'}
                        onChange={() => setLineMode('items')}
                        data-testid="line-mode-items"
                      />
                      <span>Line Items</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="line-mode"
                        checked={lineMode === 'amount'}
                        onChange={() => setLineMode('amount')}
                        data-testid="line-mode-amount"
                      />
                      <span>Direct Amount</span>
                    </label>
                  </div>
                </div>
                {lineMode === 'items' && (
                  <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Line
                  </Button>
                )}
              </div>
              {lineMode === 'amount' ? (
                <div className="border rounded-lg p-4 bg-gray-50 flex items-end gap-4">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-600">Amount (INR)</Label>
                    <Input
                      type="number"
                      value={directAmount}
                      onChange={(e) => setDirectAmount(e.target.value)}
                      placeholder="0.00"
                      className="h-10 text-lg font-mono"
                      data-testid="input-direct-amount"
                    />
                  </div>
                  <div className="text-xs text-gray-500 pb-2">
                    Posts as a single line using the reason as description.
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs text-gray-600">
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right w-20">Qty</th>
                      <th className="px-3 py-2 text-right w-28">Rate</th>
                      <th className="px-3 py-2 text-right w-28">Amount</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-2">
                          <Input
                            value={it.product_name}
                            onChange={(e) => updateItem(idx, 'product_name', e.target.value)}
                            placeholder="Line description"
                            className="h-8 border-0 focus-visible:ring-0"
                            data-testid={`item-desc-${idx}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            value={it.qty}
                            onChange={(e) => updateItem(idx, 'qty', e.target.value)}
                            className="h-8 text-right"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            value={it.rate}
                            onChange={(e) => updateItem(idx, 'rate', e.target.value)}
                            className="h-8 text-right"
                          />
                        </td>
                        <td className="px-2 py-2 text-right font-mono">
                          ₹{(it.amount || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {form.items.length > 1 && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => removeItem(idx)}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
              <div className="flex justify-end mt-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="text-xl font-bold" data-testid="total-amount">
                    ₹{Math.round(totalAmount || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Additional Notes (Optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any additional details"
                data-testid="input-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-note-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Post to Ledger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DebitCreditNotesPage;
